/**
 * Player pointers: space is BIG and ships are small — every remote pilot
 * within range gets a HUD marker. On screen it floats over them (name +
 * distance, fading near the range edge); when they slip off screen the
 * marker becomes an edge arrow pointing at them for a few seconds, so you
 * can keep track of a dogfight or a wingman without staring at the map.
 * Range: 100 km in open space, 1 km while on foot.
 */
import * as THREE from "three";
import type { RemoteShip } from "../net/net";

const RANGE_SPACE = 100_000;
const RANGE_FOOT = 1_000;
/** Edge arrows persist this long after the player leaves the screen. */
const EDGE_HOLD_MS = 4500;
const EDGE_FADE_MS = 1300;

interface Entry {
  el: HTMLElement;
  nameEl: HTMLElement;
  distEl: HTMLElement;
  arrowEl: HTMLElement;
  lastOnscreen: number;
  /** Eased presence — tags FADE in/out instead of popping. */
  alpha: number;
  want: number;
}

export class PlayerMarkers {
  private root: HTMLElement;
  private entries = new Map<number, Entry>();
  private v = new THREE.Vector3();

  constructor() {
    this.root = document.createElement("div");
    this.root.id = "player-markers";
    document.body.appendChild(this.root);
  }

  private entry(id: number, name: string, party: boolean): Entry {
    let e = this.entries.get(id);
    if (!e) {
      const el = document.createElement("div");
      el.className = "pm";
      el.innerHTML = `<span class="pm-arrow">▲</span><span class="pm-chevron">▼</span><b></b><i></i>`;
      this.root.appendChild(el);
      el.style.opacity = "0";
      e = {
        el,
        nameEl: el.querySelector("b")!,
        distEl: el.querySelector("i")!,
        arrowEl: el.querySelector(".pm-arrow")!,
        lastOnscreen: 0,
        alpha: 0,
        want: 0,
      };
      this.entries.set(id, e);
    }
    e.nameEl.textContent = name;
    e.el.classList.toggle("pm-party", party);
    return e;
  }

  update(
    remotes: Map<number, RemoteShip> | null,
    focusSys: THREE.Vector3,
    origin: THREE.Vector3,
    camera: THREE.Camera,
    onFoot: boolean,
    active: boolean,
    now: number,
    up: THREE.Vector3,
    /** Room player ids of party members — always-on, party-tinted markers. */
    partyIds: Set<number> | null = null,
  ): void {
    for (const e of this.entries.values()) e.want = 0;
    if (remotes && active) {
      const range = onFoot ? RANGE_FOOT : RANGE_SPACE;
      const W = window.innerWidth;
      const H = window.innerHeight;
      for (const [id, r] of remotes) {
        const dist = r.sysPos.distanceTo(focusSys);
        // Party members are never culled by range — that's what makes them
        // findable across a system — only by the too-close deadzone.
        const party = partyIds?.has(id) ?? false;
        if ((dist > range * 1.05 && !party) || dist < 4) continue;
        const e = this.entry(id, r.name, party);
        // Fade in close, fade out toward the range edge (party: no fade).
        const edgeFade = party ? 1 : Math.min(1, Math.max(0, (range - dist) / (range * 0.18)));
        const nearFade = Math.min(1, (dist - 4) / 16);
        this.v.copy(r.sysPos).sub(origin);
        this.v.addScaledVector(up, r.kind === "foot" ? 2.5 : 7);
        this.v.project(camera);
        const behind = this.v.z > 1;
        const onscreen = !behind && Math.abs(this.v.x) < 0.93 && Math.abs(this.v.y) < 0.9;
        const km = dist >= 1000 ? `${(dist / 1000).toFixed(dist > 10000 ? 0 : 1)} km` : `${Math.round(dist)} m`;
        e.distEl.textContent = km;
        if (onscreen) {
          e.lastOnscreen = now;
          e.el.classList.remove("pm-edge");
          e.want = 0.9 * edgeFade * nearFade;
          e.el.style.transform = `translate(${((this.v.x + 1) / 2) * W}px, ${((1 - this.v.y) / 2) * H - 34}px) translate(-50%, -100%)`;
        } else if (party || (now - e.lastOnscreen < EDGE_HOLD_MS && e.lastOnscreen > 0)) {
          // Edge arrow: clamp the (sign-corrected) NDC direction to the rim.
          let nx = behind ? -this.v.x : this.v.x;
          let ny = behind ? -this.v.y : this.v.y;
          const m = Math.max(Math.abs(nx), Math.abs(ny), 1e-6);
          nx /= m;
          ny /= m;
          const px = ((nx * 0.92 + 1) / 2) * W;
          const py = ((1 - ny * 0.88) / 2) * H;
          const ang = Math.atan2(-(ny), nx); // screen-space, y down
          // Party arrows persist (the ShipMarker rule) — no hold timer.
          const hold = party
            ? 1
            : 1 - Math.max(0, (now - e.lastOnscreen - (EDGE_HOLD_MS - EDGE_FADE_MS)) / EDGE_FADE_MS);
          e.el.classList.add("pm-edge");
          e.want = 0.85 * edgeFade * hold;
          e.el.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%)`;
          e.arrowEl.style.transform = `rotate(${ang}rad)`;
        }
      }
    }
    // Fades: ease toward the wanted opacity; reap once fully faded.
    for (const [id, e] of this.entries) {
      e.alpha += (e.want - e.alpha) * (e.want > e.alpha ? 0.16 : 0.1);
      if (e.want <= 0 && e.alpha < 0.02) {
        e.el.remove();
        this.entries.delete(id);
        continue;
      }
      e.el.style.opacity = String(e.alpha);
      e.el.style.display = e.alpha > 0.02 ? "flex" : "none";
    }
  }
}

/**
 * The player's OWN ship gets the same treatment while they're on foot —
 * with one difference: the edge arrow is PERSISTENT (no hold timer),
 * because an inbound recall approaches from over the horizon and the whole
 * point is knowing where it will appear from before it's ever been seen.
 */
export class ShipMarker {
  private el: HTMLElement;
  private distEl: HTMLElement;
  private arrowEl: HTMLElement;
  private v = new THREE.Vector3();
  private alpha = 0;

  /** Ease presence; hides under 0.02 — every tag fades, none pop. */
  private fade(want: number): void {
    this.alpha += (want - this.alpha) * (want > this.alpha ? 0.16 : 0.1);
    this.el.style.opacity = String(this.alpha);
    this.el.style.display = this.alpha > 0.02 ? "flex" : "none";
  }

  constructor() {
    this.el = document.createElement("div");
    this.el.className = "pm pm-ship";
    this.el.innerHTML = `<span class="pm-arrow">▲</span><span class="pm-chevron">▼</span><b>SHIP</b><i></i>`;
    this.distEl = this.el.querySelector("i")!;
    this.arrowEl = this.el.querySelector(".pm-arrow")!;
    document.getElementById("player-markers")?.appendChild(this.el) ??
      document.body.appendChild(this.el);
  }

  update(
    shipSysPos: THREE.Vector3 | null,
    focusSys: THREE.Vector3,
    origin: THREE.Vector3,
    camera: THREE.Camera,
    active: boolean,
    up: THREE.Vector3,
  ): void {
    if (!shipSysPos || !active) {
      this.fade(0);
      return;
    }
    const dist = shipSysPos.distanceTo(focusSys);
    if (dist < 4 || dist > 1500) {
      // At the boarding ramp the prompt has it; past 1.5 km the recall
      // flow (which brings the ship to YOU) is the pointer.
      this.fade(0);
      return;
    }
    const W = window.innerWidth;
    const H = window.innerHeight;
    this.v.copy(shipSysPos).sub(origin);
    this.v.addScaledVector(up, 3.2); // just over the hull, along LOCAL up
    this.v.project(camera);
    const behind = this.v.z > 1;
    const onscreen = !behind && Math.abs(this.v.x) < 0.93 && Math.abs(this.v.y) < 0.9;
    this.distEl.textContent =
      dist >= 1000 ? `${(dist / 1000).toFixed(dist > 10000 ? 0 : 1)} km` : `${Math.round(dist)} m`;
    if (onscreen) {
      this.el.classList.remove("pm-edge");
      this.el.style.transform = `translate(${((this.v.x + 1) / 2) * W}px, ${((1 - this.v.y) / 2) * H - 34}px) translate(-50%, -100%)`;
      this.fade(0.9 * Math.min(1, (dist - 4) / 16));
    } else {
      // Persistent edge arrow at the screen rim, pointing at the ship.
      let nx = behind ? -this.v.x : this.v.x;
      let ny = behind ? -this.v.y : this.v.y;
      const m = Math.max(Math.abs(nx), Math.abs(ny), 1e-6);
      nx /= m;
      ny /= m;
      const px = ((nx * 0.92 + 1) / 2) * W;
      const py = ((1 - ny * 0.88) / 2) * H;
      const ang = Math.atan2(-ny, nx); // screen-space, y down
      this.el.classList.add("pm-edge");
      this.el.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%)`;
      this.arrowEl.style.transform = `rotate(${ang}rad)`;
      this.fade(0.85);
    }
  }
}
